import { toast } from 'react-toastify';

// Generate unique toast IDs to prevent conflicts
let toastCounter = 0;
const generateToastId = (prefix = 'toast') => `${prefix}-${Date.now()}-${++toastCounter}`;

// Toast configuration
const toastConfig = {
  position: 'top-right',
  autoClose: 3000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: false,
  pauseOnFocusLoss: false,
  draggable: true,
  progress: undefined,
  theme: 'light',
  closeButton: true
};

// Track active toasts
const activeToasts = new Map();

// Success toast
export const showSuccess = (message) => {
  const toastId = generateToastId('success');
  return toast.success(message, { ...toastConfig, toastId });
};

// Error toast
export const showError = (message) => {
  const toastId = generateToastId('error');
  return toast.error(message, { ...toastConfig, toastId });
};

// Warning toast
export const showWarning = (message) => {
  const toastId = generateToastId('warning');
  return toast.warning(message, { ...toastConfig, toastId });
};

// Info toast
export const showInfo = (message) => {
  const toastId = generateToastId('info');
  return toast.info(message, { ...toastConfig, toastId });
};

// Loading toast
export const showLoading = (message) => {
  // Dismiss any existing loading toasts
  dismissAllToasts();
  
  const toastId = generateToastId('loading');
  const loadingToastId = toast.loading(message, {
    ...toastConfig,
    isLoading: true,
    autoClose: false,
    closeOnClick: false,
    closeButton: false,
    draggable: false,
    toastId
  });
  
  activeToasts.set('loading', loadingToastId);
  return loadingToastId;
};

// Update loading toast to success
export const updateToSuccess = (toastId, message) => {
  // Dismiss any existing loading toasts
  if (activeToasts.has('loading')) {
    toast.dismiss(activeToasts.get('loading'));
    activeToasts.delete('loading');
  }
  
  if (toastId) {
    return toast.update(toastId, {
      render: message,
      type: 'success',
      isLoading: false,
      autoClose: 2000,
      closeButton: true,
      draggable: true,
      toastId: generateToastId('success-update')
    });
  }
  
  return toast.success(message, {
    ...toastConfig,
    autoClose: 2000,
    toastId: generateToastId('success-fallback')
  });
};

// Update loading toast to error
export const updateToError = (toastId, message) => {
  // Dismiss any existing loading toasts
  if (activeToasts.has('loading')) {
    toast.dismiss(activeToasts.get('loading'));
    activeToasts.delete('loading');
  }
  
  if (toastId) {
    return toast.update(toastId, {
      render: message,
      type: 'error',
      isLoading: false,
      autoClose: 3000,
      closeButton: true,
      draggable: true,
      toastId: generateToastId('error-update')
    });
  }
  
  return toast.error(message, {
    ...toastConfig,
    autoClose: 3000,
    toastId: generateToastId('error-fallback')
  });
};

// Dismiss toast
export const dismissToast = (toastId, force = false) => {
  if (!toastId) return;
  
  try {
    // Check if toast is active before trying to dismiss
    const isActive = toast.isActive(toastId);
    if (isActive || force) {
      toast.dismiss(toastId);
    }
    
    // Clean up from active toasts map
    for (const [key, value] of activeToasts.entries()) {
      if (value === toastId) {
        activeToasts.delete(key);
        break;
      }
    }
  } catch (error) {
    console.warn('Error dismissing toast:', error);
    // Force dismiss if there was an error
    if (force) {
      try {
        toast.dismiss(toastId);
      } catch (e) {
        // Last resort: clear all toasts if we can't dismiss a specific one
        toast.dismiss();
      }
    }
  }
};

// Dismiss all toasts
export const dismissAllToasts = () => {
  try {
    toast.dismiss();
    activeToasts.clear();
  } catch (error) {
    console.warn('Error dismissing all toasts:', error);
  }
};

// Clear specific toast type
export const clearToastType = (type) => {
  if (activeToasts.has(type)) {
    toast.dismiss(activeToasts.get(type));
    activeToasts.delete(type);
  }
};